// src/components/TermsOfService.jsx
import React from 'react';
import { FileText, Hand, AlertTriangle, CheckCircle } from 'lucide-react';

function TermsOfService() {
  const lastUpdated = "December 9, 2025";

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-12 pt-16">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <FileText className="w-16 h-16 text-purple-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              These terms govern your use of Connect & Co and outline our mutual rights and responsibilities.
            </p>
            <p className="text-sm text-gray-500 mt-4">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </div>  
 
      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">

          {/* Quick Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-800 mb-3">📋 Quick Summary</h3>
            <p className="text-blue-700 text-sm leading-relaxed">
              By using Connect & Co, you agree to be respectful to our community, follow our community and event guidelines, 
              and understand that we're a platform connecting people for events and experiences. By signing up 
              or registering for any event, you'll be automatically added to our mailing list for community updates 
              (you can always unsubscribe). We're committed to providing a safe, inclusive space for locals, expats, professionals, 
              entrepreneurs, and culture enthusiasts worldwide.
            </p>
          </div>

          {/* Acceptance of Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              By accessing or using Connect & Co's website, mobile application, or services (collectively, the "Service"), 
              you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, 
              you may not access the Service.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Connect & Co is operated by Pukka Holdings LLC, a Florida corporation. 
              These Terms constitute a legal agreement between you and Pukka Holdings LLC
            </p>


            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Note on Branding:</strong> Throughout our communications, we may refer to our 
              platform as "Connect & Co," "ConnectCo,", "FikaConnect", or simply "Fika." These all refer to the 
              same service operated by Pukka Holdings LLC
            </p>

            
            <p className="text-gray-700 leading-relaxed">
              These Terms apply to all visitors, users, and others who access or use the Service, including event hosts, 
              attendees, and community members.
            </p>
          </section>

          {/* Description of Service */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Connect & Co is a global community platform that provides:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span>Access to exclusive events, networking opportunities, and cultural experiences</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span>Community features for connecting with locals, expats, digital nomads, and professionals</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span>Priority access and early RSVP opportunities for members</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span>Event discovery and recommendation services</span>
              </li>
            </ul>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Creation</h3>
            <p className="text-gray-700 mb-4">
              To access certain features, you must create an account. You agree to provide accurate, current, 
              and complete information during registration and update your information to keep it accurate.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Security</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>You are responsible for safeguarding your account credentials</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Notify us immediately of any unauthorized access</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>You're responsible for all activities under your account</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Age Requirement</h3>
            <p className="text-gray-700">
              You must be at least 18 years old to use our Service. By using the Service, you represent that you meet this age requirement.
            </p>
          </section>

          {/* Prohibited Uses */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Prohibited Uses</h2>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <AlertTriangle className="w-6 h-6 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-red-800 mb-2">Community Standards</h3>
                  <p className="text-red-700 text-sm">
                    Connect & Co is built on respect, inclusivity, and positive community interactions. 
                    Violations may result in immediate account suspension.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">You may not use our Service to:</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-medium text-gray-800">Harmful Behavior</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Harass, intimidate, or discriminate against others</li>
                  <li>• Share inappropriate or offensive content</li>
                  <li>• Engage in fraudulent or deceptive practices</li>
                  <li>• Violate any laws or regulations</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium text-gray-800">Platform Misuse</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Create fake accounts or impersonate others</li>
                  <li>• Spam or send unsolicited communications</li>
                  <li>• Attempt to gain unauthorized access</li>
                  <li>• Interfere with Service functionality</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium text-gray-800">Community Standards</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Actively undermine community values or purposefully disrupt events</li>
                  <li>• Engage in coordinated efforts to harm the community or its reputation</li>
                  <li>• Persistently create hostile or negative environments for other members</li>
                  <li>• Publicly misrepresent the community or its founder with false information</li>
                  <li>• Recruit members to boycott, leave, or act against community interests</li>
                </ul>
              </div>

            </div>
          </section>

          {/* Event Policies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Event Policies</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Event Participation</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>RSVP Accuracy:</strong> Only RSVP to events you genuinely plan to attend</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Cancellation Policy:</strong> Update your RSVP if plans change to allow others to attend</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Event Conduct:</strong> Treat all attendees with respect and follow venue rules</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Automatic Mailing List Enrollment</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-700 text-sm leading-relaxed">
                <strong>Email Signup Policy:</strong> By signing up for Connect & Co or registering for any event, 
                your email address will be automatically added to our community mailing list to receive event notifications, 
                community updates, priority access opportunities, and exclusive member benefits. You can unsubscribe at any time using the link in any email we send.
              </p>
            </div>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Registration Tracking:</strong> All signups and event registrations are recorded for community analytics and member benefits</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Email Communications:</strong> We'll send you updates about upcoming events, priority access opportunities, community highlights, and member-exclusive content</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Event Check-In Requirement</h3>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <p className="text-purple-700 text-sm leading-relaxed">
                <strong>Mandatory QR Code Scanning:</strong> All registered attendees must check in at each event by scanning the QR code displayed on designated tables at the venue. This check-in process is required for attendance tracking, member passport stamp verification, and community analytics. Failure to complete check-in may result in your RSVP being marked as a no-show.
              </p>
            </div>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Check-In Location:</strong> QR codes are displayed on tables at the event venue entrance or designated check-in area</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Technical Requirements:</strong> You must have access to connectco.app on your mobile device to complete check-in</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Attendance Verification:</strong> Check-in confirmation is required to receive member passport stamps and maintain accurate attendance records</span>
              </li>
            </ul>

            

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Photography Policy</h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-700 text-sm leading-relaxed">
                <strong>Event Photography:</strong> Event photography will be conducted for promotional use on our website and social media platforms. By attending, you consent to being photographed unless you notify our organizers of your preference to opt out upon arrival.
              </p>
            </div>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Promotional Use:</strong> Photos may be used on Connect & Co website, social media, and marketing materials</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Opt-Out Option:</strong> Inform event organizers upon arrival if you prefer not to be photographed</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Respectful Photography:</strong> All photography will be conducted professionally and respectfully</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Event Content</h3>
            <p className="text-gray-700">
              Event hosts are responsible for their event content and comply with all applicable laws. 
              Connect & Co reserves the right to remove events that violate these Terms or our community standards.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3"><br></br>Event Capacity and RSVP Availability</h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-700 text-sm leading-relaxed">
                  <strong>No Guaranteed Reservations:</strong> While premium membership provides access to all Connect & Co events and experiences, it does not guarantee availability for specific events. All events operate on a first-come, first-served basis for paid subscribers. Once an event reaches capacity, no additional RSVPs will be accepted, regardless of membership status.
                </p>
              </div>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>Capacity Limits:</strong> Events have predetermined capacity limits based on venue restrictions and experience quality standards</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>RSVP Priority:</strong> All paid subscribers have equal opportunity to RSVP on a first-come, first-served basis when event registration opens</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>Sold-Out Events:</strong> When an event is fully booked, a waitlist may be available. Membership does not guarantee conversion from waitlist to confirmed attendance</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>Early Access:</strong> Six-month and yearly subscribers receive priority notification of new events but must still RSVP promptly to secure their spot</span>
                </li>
              </ul>


          </section>

          {/* Subscription Agreement - NEW SECTION */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Subscription Agreement</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Premium Membership Plans</h3>
            <p className="text-gray-700 mb-4">
              Connect & Co offers premium membership subscriptions that provide access to exclusive events, 
              experiences, and community features. By purchasing a subscription, you agree to the following terms:
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Available Plans</h3>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>Monthly Premium:</strong> ¥2,100 per month, billed monthly</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>6-Month Premium:</strong> ¥11,340 per 6 months, billed every 6 months (10% savings)</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>Yearly Premium:</strong> ¥21,420 per year, billed annually (15% savings + complimentary Fika T-shirt)</span>
                </li>
              </ul>
            </div>
            <p className="text-gray-700 text-sm mb-4">
              Pricing is subject to change with 30 days advance notice to active subscribers. 
              Current subscribers will maintain their existing rate until their next renewal period.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Billing and Payment</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Payment Processing:</strong> All payments are processed securely through Stripe. We accept major credit cards and debit cards.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Auto-Renewal:</strong> Subscriptions automatically renew at the end of each billing period unless cancelled before the renewal date.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Billing Date:</strong> You will be charged on the date you initially subscribed and on the same date each subsequent billing period.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Failed Payments:</strong> If your payment method fails, we will attempt to process payment again. Continued payment failure may result in suspension of your subscription.</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Subscription Benefits</h3>
            <p className="text-gray-700 mb-4">
              Premium membership includes access to all Connect & Co experiences and events:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <h4 className="font-medium text-gray-800">Regular Events</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Weekly Signature Fika (every Wednesday)</li>
                  <li>• Monthly Fika community gatherings</li>
                  <li>• Curated Fika (AI-matched 3-4 person sessions)</li>
                  <li>• Builder Coffee Match (1-on-1 for founders)</li>
                  <li>• Goal Accountability Partners</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium text-gray-800">Special Experiences</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Speed Fika events</li>
                  <li>• Happy Hours and Wine Events</li>
                  <li>• Curated Dinners & Brunches</li>
                  <li>• River Walks and cultural experiences</li>
                  <li>• Founders 1-on-1 sessions</li>
                </ul>
              </div>
            </div>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span><strong>Member Passport:</strong> Automatic tracking of event attendance with tier progression (Community Member → Trusted Member → Community Champion → Community Catalyst)</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span><strong>Priority Registration:</strong> Early access to event RSVPs for 6-month and yearly subscribers</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <span><strong>Exclusive Perks:</strong> Yearly subscribers receive a complimentary Fika T-shirt (¥4,000 value)</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Cancellation Policy</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-700 text-sm leading-relaxed mb-2">
                <strong>Cancel Anytime:</strong> You may cancel your subscription at any time through your subscription management page or by contacting support at hello@connectco.app.
              </p>
              <p className="text-blue-700 text-sm leading-relaxed">
                <strong>Access Continuation:</strong> Upon cancellation, you will retain full access to all premium benefits until the end of your current paid billing period. No partial refunds are provided for the current billing cycle.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Refund Policy</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>No Partial Refunds:</strong> Subscription fees are non-refundable  or at our sole discretion.</span>
              </li>
              {/*
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Exception Cases:</strong> Refunds may be considered within 7 days of initial subscription purchase if no events have been attended and no services have been used.</span>
              </li> 
              */}
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Event Cancellations:</strong> If Connect & Co cancels an event, no refund is provided, but members receive priority access to similar future events.</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Plan Changes</h3>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Upgrades:</strong> You may upgrade your subscription plan at any time. Upgrades take effect immediately, and you will be charged the prorated difference.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Downgrades:</strong> Plan downgrades take effect at the start of your next billing cycle. Contact support to request a downgrade.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Subscription Extensions:</strong> You may purchase additional subscription time at your current plan rate at any time.</span>
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Management</h3>
            <p className="text-gray-700 mb-4">
              No separate account creation is required. Your subscription is managed via your email address. 
              You can access subscription management features by visiting our subscription page and entering your email, 
              or by using the links provided in subscription confirmation emails.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Subscription Suspension or Termination</h3>
            <p className="text-gray-700 mb-4">
              Connect & Co reserves the right to suspend or terminate your subscription if you:
            </p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Violate these Terms of Service or community guidelines</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Engage in disruptive, harmful, or inappropriate behavior at events</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Provide false information or engage in fraudulent activity</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span>Repeatedly fail to attend events you've RSVP'd to without notice</span>
              </li>
            </ul>
            <p className="text-gray-700">
              In cases of termination for policy violations, no refund will be provided for the remaining subscription period.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Our Content</h3>
            <p className="text-gray-700 mb-4">
              The Service and its original content, features, and functionality are owned by Pukka Holdings LLC and protected by international copyright, trademark, and other intellectual property laws.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">User Content</h3>
            <p className="text-gray-700">
              You retain ownership of content you submit, but grant us a license to use, display, and distribute your content in connection with the Service. You represent that you have the right to share any content you submit.
            </p>
          </section>

          {/* Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Privacy</h2>
            <p className="text-gray-700">
              Your privacy is important to us. Please review our Privacy Policy, which explains how we collect, use, and protect your information. By using the Service, you agree to our privacy practices.
            </p>
          </section>

          {/* Disclaimers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Disclaimers</h2>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Service Availability</h3>
              <p className="text-yellow-700 text-sm">
                The Service is provided "as is" without warranties. We don't guarantee uninterrupted access and may modify or discontinue features at any time.
              </p>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Third-Party Events & Venues</h3>
            <p className="text-gray-700 mb-4">
              Connect & Co facilitates connections to events but is not responsible for third-party venues, event hosts, or attendee behavior. Attend events at your own risk and follow all applicable safety guidelines.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Beta Service</h3>
            <p className="text-gray-700">
              Parts of our Service may be in beta testing. Beta features may be unstable, changed, or discontinued without notice.
            </p>

            {/* Community Content and Interactions */}
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Community Content and Interactions</h3>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-700 text-sm leading-relaxed">
                <strong>Important:</strong> Connect & Co facilitates community connections but does not control individual member behavior or event content. Exercise good judgment when interacting with community members.
              </p>
            </div>

            

            <h4 className="text-lg font-medium text-gray-800 mb-2">Content of Community Members</h4>
            <p className="text-gray-700 mb-4">
              Connect & Co does not control the content shared by community members. When we become aware of inappropriate content on our platform, we reserve the right to investigate and take appropriate action, but we do not have any obligation to monitor, nor do we take responsibility for, the content shared by other members.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">Member Interactions and Events</h4>
            <p className="text-gray-700 mb-4">
              Connect & Co is not a party to any arrangements made through our platform, including event attendance, meetups, or other interactions. Connect & Co does not conduct or require background checks on members and does not attempt to verify the truth or accuracy of statements made by members. Connect & Co makes no representations or warranties concerning the conduct or content of any members or their interactions with you.
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-green-700 text-sm leading-relaxed">
                <strong>💡 Community Tip:</strong> Exercise common sense and good judgment when using our platform and interacting with other members, both online and at Connect & Co events. Trust your instincts and prioritize your safety.
              </p>
            </div>

            <h4 className="text-lg font-medium text-gray-800 mb-2">Relationships and Connections Formed Through Events</h4>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <AlertTriangle className="w-6 h-6 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-red-700 text-sm leading-relaxed mb-2">
                    <strong>Important Disclaimer:</strong> Connect & Co is not responsible for any friendships, romantic relationships, business partnerships, or other connections that develop between members during or after our events and experiences.
                  </p>
                </div>
              </div>
            </div>

            <ul className="space-y-2 text-gray-700 mb-4">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>No Vetting Process:</strong> Connect & Co does not conduct background checks, verify identities, or screen members beyond basic email account registration. We do not guarantee the accuracy of any information members provide about themselves.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Personal Responsibility:</strong> You are solely responsible for your interactions with other members, both during events and in any ongoing relationships that may develop afterward.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Relationship Outcomes:</strong> Connect & Co is not liable for any disputes, damages, losses, injuries, or negative outcomes arising from relationships formed through our platform or events, whether personal, professional, romantic, or otherwise.</span>
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-purple-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <span><strong>Exercise Caution:</strong> We strongly encourage members to exercise good judgment, meet in public places, protect personal information, and trust their instincts when forming new connections.</span>
              </li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mb-2">Reporting Concerns</h4>
            <p className="text-gray-700">
              If you have concerns regarding other members or inappropriate behavior, please report it to <a href="mailto:hello@connectco.app" className="text-purple-600 hover:text-purple-700">hello@connectco.app</a>. We take community safety seriously and will investigate all reports promptly.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              Pukka Holdings LLC's liability is limited to the maximum extent permitted by law. We are not liable for indirect, incidental, or consequential damages arising from your use of the Service.
            </p>
            <p className="text-gray-700">
              Our total liability for any claims related to the Service will not exceed the amount you paid us in the twelve months preceding the claim.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Termination</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Rights</h3>
            <p className="text-gray-700 mb-4">
              You may terminate your account at any time by contacting us or using account deletion features in your settings.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Our Rights</h3>
            <p className="text-gray-700">
              We may suspend or terminate your account immediately if you violate these Terms, engage in harmful behavior, or for any reason that protects our community or Service integrity.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
            <p className="text-gray-700">
              These Terms are governed by the laws of the State of Florida without regard to conflict of law principles. Any disputes will be resolved through binding arbitration or in the state or federal courts located in Florida.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Changes to Terms</h2>
            <p className="text-gray-700">
              We may update these Terms periodically. Material changes will be communicated via email or prominent notice on our Service. Continued use after changes constitutes acceptance of the updated Terms.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact Us</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                Questions about these Terms? We're here to help:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Legal Entity:</strong> Pukka Holdings LLC</p>
                <p><strong>Jurisdiction:</strong> Florida, USA</p>
                <p><strong>Email:</strong> <a href="mailto:hello@connectco.app" className="text-purple-600 hover:text-purple-700">hello@connectco.app</a></p>
                <p><strong>Subject Line:</strong> Terms of Service Inquiry</p>
                <p><strong>Response Time:</strong> We respond to all legal inquiries within 72 hours</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;